import { useQuery } from "@apollo/client";
import { Delete, Edit } from "@mui/icons-material";
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
} from "@mui/material";
import { ReactElement, useState } from "react";
import { useParams } from "react-router-dom";

import {
  ActBlueWebhookIntegration,
  AllIntegrationTypes,
  DeleteAudienceContactFieldMutation,
  GetAudienceContactFieldsQuery,
  GetAudienceContactFieldsQueryVariables,
  GetSubaccountShortcodeQuery,
  GetSubaccountShortcodeQueryVariables,
  IntegrationServiceType,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import Table from "~frontend/components/Table";
import {
  DELETE_AUDIENCE_CONTACT_FIELD,
  GET_AUDIENCE_CONTACT_FIELDS,
} from "~frontend/graphql/audience";
import { GET_SUBACCOUNT_SHORTCODE } from "~frontend/graphql/shortcode";
import { useFlagRedirect } from "~frontend/hooks/useFlagRedirect";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import useMutation from "~frontend/util/useMutation";

import { ActBlueIntegrations } from "../shortcode/ActBlueIntegrations";
import FixedKeywords from "../shortcode/FixedKeywords";
import OptInKeywords from "../shortcode/OptInKeywords";
import { AddOrEditField } from "../shortcode/Subscribers/AddOrEditField";
import { FieldTypesByDbType } from "../shortcode/Subscribers/ViewFields";
import { sortContactFields } from "../shortcode/Subscribers/util";

const tableSx = {
  ".MuiTableCell-root": {
    borderBottom: `1px solid ${palette.lightGray}`,
    py: 1,
  },
};

function ContactFieldsContainer({
  shortcodeId,
  subaccountId,
  audienceId,
}: {
  shortcodeId: string;
  subaccountId: string;
  audienceId: string;
}): ReactElement {
  const customFieldsAllowed = useFeatureFlag("audienceCustomFields");

  const {
    data: audienceFields,
    loading,
    error,
  } = useQuery<
    GetAudienceContactFieldsQuery,
    GetAudienceContactFieldsQueryVariables
  >(GET_AUDIENCE_CONTACT_FIELDS, {
    variables: {
      input: { opnId: shortcodeId, subaccountId: subaccountId },
    },
  });

  const [deleteContactField] = useMutation<DeleteAudienceContactFieldMutation>(
    DELETE_AUDIENCE_CONTACT_FIELD,
    {
      refetchQueries: [GET_AUDIENCE_CONTACT_FIELDS],
      awaitRefetchQueries: true,
    }
  );

  const [editingField, setEditingField] = useState<
    | GetAudienceContactFieldsQuery["getAudienceContactFields"][number]
    | "NEW"
    | null
  >(null);

  return (
    <FlexBox flexDirection="column">
      {editingField && (
        <Dialog
          open
          onClose={() => setEditingField(null)}
          fullWidth
          maxWidth="sm"
        >
          <DialogContent>
            <AddOrEditField
              audienceId={audienceId}
              field={
                editingField === "NEW"
                  ? "NEW"
                  : {
                      id: editingField.id,
                      name: editingField.name,
                      type: editingField.type,
                      typeParameters: editingField.typeParameters,
                    }
              }
              onClose={() => setEditingField(null)}
              existingFields={audienceFields?.getAudienceContactFields || []}
            />
          </DialogContent>
        </Dialog>
      )}

      <PageHeader>Contact Fields</PageHeader>
      <FlexBox direction="column" gap={3}>
        <Card>
          <CardHeader title="Standard Fields" />
          <CardContent
            sx={{ px: 2, py: 1, backgroundColor: palette.lightestGray }}
          >
            <AsyncWrapper
              data={audienceFields}
              loading={loading}
              errors={error}
            >
              {({ getAudienceContactFields }) => (
                <Table<
                  GetAudienceContactFieldsQuery["getAudienceContactFields"][number]
                >
                  size="small"
                  columns={[
                    {
                      title: "Field Name",
                      key: "label",
                      sx: { width: "180px" },
                      renderValue: field => field.displayName || field.name,
                    },
                    {
                      title: "Field Type",
                      key: "type",
                      renderValue: field =>
                        FieldTypesByDbType[field.type]?.label || field.type,
                      align: "left",
                    },
                  ]}
                  rows={sortContactFields(
                    getAudienceContactFields.filter(field => !field.isEditable)
                  )}
                  containerSx={tableSx}
                  noFooter
                />
              )}
            </AsyncWrapper>
          </CardContent>
        </Card>
        {customFieldsAllowed && (
          <Card
            sx={{ border: 0, backgroundColor: palette.lightestGray }}
            data-testid="custom-fields-card"
          >
            <CardHeader title="Custom Fields" />
            <CardContent sx={{ px: 2, py: 1, backgroundColor: palette.white }}>
              <AsyncWrapper
                data={audienceFields}
                loading={loading}
                errors={error}
              >
                {({ getAudienceContactFields }) => (
                  <Table<
                    GetAudienceContactFieldsQuery["getAudienceContactFields"][number]
                  >
                    rowActions={[
                      {
                        id: "edit-field",
                        type: "button",
                        icon: <Edit />,
                        tooltipLabel: "Edit field",
                        onClick: ({ id }) => {
                          const field = getAudienceContactFields.find(
                            field => field.id === id
                          );
                          if (field && field.isEditable) {
                            setEditingField(field);
                          }
                        },
                      },
                      {
                        id: "delete-field",
                        type: "button",
                        icon: <Delete />,
                        tooltipLabel: "Delete field",
                        confirm: {
                          title: "Delete Custom Field",
                          description:
                            "Are you sure you want to delete this custom field?",
                        },
                        onClick: async ({ id }) => {
                          await deleteContactField({
                            variables: {
                              input: { id, subaccountId: subaccountId },
                            },
                          });
                        },
                      },
                    ]}
                    size="small"
                    columns={[
                      {
                        title: "Field Name",
                        key: "name",
                        sx: { width: "180px" },
                        renderValue: field => field.name,
                      },
                      {
                        title: "Field Type",
                        key: "type",
                        renderValue: field =>
                          FieldTypesByDbType[field.type]?.label || field.type,
                        align: "left",
                      },
                    ]}
                    rows={sortContactFields(
                      getAudienceContactFields.filter(field => field.isEditable)
                    )}
                    noFooter
                    emptyState="No custom fields found."
                    actionSx={{ ".MuiButtonBase-root": { py: "0", px: "6px" } }}
                    containerSx={tableSx}
                  />
                )}
              </AsyncWrapper>
              <FlexBox justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{ fontSize: "10px" }}
                  onClick={() => setEditingField("NEW")}
                >
                  + Add Custom Field
                </Button>
              </FlexBox>
            </CardContent>
          </Card>
        )}
      </FlexBox>
    </FlexBox>
  );
}

export function AudienceSettingsPage(): ReactElement {
  useFlagRedirect("useAudiencesV2Page", "/shortcode");
  const { subaccount, subaccountLink } = useSubaccount()!;

  const { audienceId } = useParams<{
    audienceId: string;
  }>();

  useBreadcrumbs([
    {
      label: "Audiences",
      path: subaccountLink("/audiences"),
    },
    {
      label: "Settings",
      path: subaccountLink(`/audiences/${audienceId}/settings`),
    },
  ]);

  // temporary: once we have generic audiences, we will need to provide a list of all audiences this subaccount has
  // right now, we can assume there is only one audience per subaccount (but we need to check if it matches the existing shortcode's audience ID)
  const {
    data: shortcodeData,
    loading,
    error,
  } = useQuery<
    GetSubaccountShortcodeQuery,
    GetSubaccountShortcodeQueryVariables
  >(GET_SUBACCOUNT_SHORTCODE, {
    variables: {
      input: {
        subaccountId: subaccount.id,
      },
    },
  });

  if (!audienceId) {
    return <Alert severity="error">No audience found.</Alert>;
  }

  return (
    <PageContainer>
      <AsyncWrapper data={shortcodeData} loading={loading} errors={error}>
        {({ getSubaccount }) => {
          const shortcode = getSubaccount.shortcode;

          if (!shortcode) {
            return (
              <Alert severity="warning">
                No shortcode found for this subaccount.
              </Alert>
            );
          }

          if (shortcode.subscriberAudienceId !== audienceId) {
            throw new Error(
              `Audience not found for phone number ${shortcode.phoneNumber}; please contact support.`
            );
          }

          return (
            <FlexBox flexDirection="column" gap={6}>
              <FixedKeywords shortcodeConfig={shortcode.shortCodeConfig!} />
              <OptInKeywords shortcodeId={shortcode.id} />
              <ActBlueIntegrations
                integrations={
                  (
                    (getSubaccount.integrations || []) as AllIntegrationTypes[]
                  ).filter(
                    integration =>
                      integration.service ===
                        IntegrationServiceType.ActBlueWebhook &&
                      (integration as ActBlueWebhookIntegration).settings
                        ?.subscribeDonors
                  ) as ActBlueWebhookIntegration[]
                }
              />
              <ContactFieldsContainer
                shortcodeId={shortcode.id}
                subaccountId={subaccount.id}
                audienceId={audienceId}
              />
            </FlexBox>
          );
        }}
      </AsyncWrapper>
    </PageContainer>
  );
}
